import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ContactRetroDual = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://formspree.io/f/xbldypok', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-[#F5E6D3] dark:bg-[#1a1a2e] relative overflow-hidden transition-colors duration-500">
      {/* Dot Grid Background - Light */}
      <div 
        className="absolute inset-0 opacity-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #8B7355 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Dot Grid Background - Dark (Glowing) */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #00ff88 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Scanline Effect - Dark Mode */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-5 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #00ff88 2px, #00ff88 4px)`
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 py-12">
        
        {/* Page Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-white dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 px-8 py-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] mb-4 transition-all duration-500">
            <h1 className="text-5xl font-black uppercase tracking-tight transition-colors duration-500">
              GET IN <span className="text-blue-600 dark:text-cyan-400 dark:drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]">TOUCH</span>
            </h1>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-green-400 max-w-2xl mx-auto transition-colors duration-500">
            📧 Let's Connect and Build Something Great
          </p>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Contact Form Window */}
          <motion.div
            className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,0,255,0.5)] transition-all duration-500"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Window Title Bar */}
            <div className="bg-gradient-to-r from-blue-400 to-cyan-500 dark:from-pink-500 dark:to-purple-600 border-b-4 border-black dark:border-pink-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                  <div className="w-4 h-4 rounded-full bg-yellow-300 border-2 border-black dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                  <div className="w-4 h-4 rounded-full bg-green-400 border-2 border-black dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
                </div>
                <span className="font-black text-lg uppercase tracking-tight ml-2 text-black dark:text-pink-300 transition-colors duration-500">📨 MESSAGE.exe</span>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-8">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name & Email Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-black uppercase text-xs mb-2 dark:text-white transition-colors duration-500">
                        NAME *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-3 border-black dark:border-cyan-400 bg-white dark:bg-[#1a1a2e] font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:focus:shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all text-black dark:text-white duration-500"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block font-black uppercase text-xs mb-2 dark:text-white transition-colors duration-500">
                        EMAIL *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-3 border-black dark:border-cyan-400 bg-white dark:bg-[#1a1a2e] font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:focus:shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all text-black dark:text-white duration-500"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block font-black uppercase text-xs mb-2 dark:text-white transition-colors duration-500">
                      MESSAGE *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border-3 border-black dark:border-cyan-400 bg-white dark:bg-[#1a1a2e] font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:focus:shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all resize-none text-black dark:text-white duration-500"
                      placeholder="Tell me about your project..."
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full bg-green-400 hover:bg-green-500 border-4 border-black dark:border-green-400 px-6 py-4 font-black text-xl uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,0,0.6)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_30px_rgba(0,255,0,0.8)] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] text-black duration-500"
                  >
                    SEND MESSAGE →
                  </button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-green-400 border-4 border-black dark:border-green-400 rounded-full flex items-center justify-center mx-auto mb-6 dark:shadow-[0_0_30px_rgba(0,255,0,0.8)] transition-all duration-500">
                    <span className="text-6xl">✓</span>
                  </div>
                  <h3 className="text-3xl font-black uppercase mb-4 dark:text-white transition-colors duration-500">
                    MESSAGE SENT!
                  </h3>
                  <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-6 transition-colors duration-500">
                    Thanks for reaching out! I'll get back to you soon.
                  </p>
                  <button
                    onClick={resetForm}
                    className="bg-blue-400 dark:bg-cyan-400 hover:bg-blue-500 dark:hover:bg-cyan-500 border-4 border-black dark:border-cyan-400 px-6 py-3 font-black uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.6)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(0,255,255,0.8)] transition-all hover:translate-x-[-1px] hover:translate-y-[-1px] text-black duration-500"
                  >
                    Send Another →
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Contact Info & Status Column */}
          <div className="space-y-8">
            
            {/* Contact Info Window */}
            <motion.div
              className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-purple-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(138,43,226,0.5)] transition-all duration-500"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Window Title Bar */}
              <div className="bg-gradient-to-r from-purple-400 to-pink-500 border-b-4 border-black dark:border-purple-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-black dark:bg-purple-400 dark:shadow-[0_0_10px_rgba(138,43,226,0.8)]"></div>
                  <span className="font-black text-lg uppercase tracking-tight text-black dark:text-purple-300 transition-colors duration-500">📞 CONTACT_INFO</span>
                </div>
              </div>

              {/* Contact Cards */}
              <div className="p-6 space-y-4">
                <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 p-4 flex items-start gap-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_10px_rgba(0,255,255,0.5)] transition-all duration-500">
                  <div className="w-12 h-12 bg-blue-400 dark:bg-cyan-400 border-3 border-black dark:border-cyan-400 flex items-center justify-center flex-shrink-0 dark:shadow-[0_0_15px_rgba(0,255,255,0.6)] transition-all duration-500">
                    <span className="text-2xl">📧</span>
                  </div>
                  <div>
                    <h3 className="font-black uppercase text-xs mb-1 dark:text-white transition-colors duration-500">EMAIL</h3>
                    <a 
                      href="mailto:cjmadigan@wisc.edu"
                      className="font-bold text-blue-600 dark:text-cyan-400 hover:underline transition-colors duration-500"
                    >
                      cjmadigan@wisc.edu
                    </a>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-pink-400 p-4 flex items-start gap-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_10px_rgba(255,0,255,0.5)] transition-all duration-500">
                  <div className="w-12 h-12 bg-green-400 dark:bg-pink-400 border-3 border-black dark:border-pink-400 flex items-center justify-center flex-shrink-0 dark:shadow-[0_0_15px_rgba(255,0,255,0.6)] transition-all duration-500">
                    <span className="text-2xl">📱</span>
                  </div>
                  <div>
                    <h3 className="font-black uppercase text-xs mb-1 dark:text-white transition-colors duration-500">PHONE</h3>
                    <a 
                      href="tel:+17083546639"
                      className="font-bold text-blue-600 dark:text-pink-400 hover:underline transition-colors duration-500"
                    >
                      (708) 354-6639
                    </a>
                  </div>
                </div>

                <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-purple-400 p-4 flex items-start gap-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_10px_rgba(138,43,226,0.5)] transition-all duration-500">
                  <div className="w-12 h-12 bg-purple-400 border-3 border-black dark:border-purple-400 flex items-center justify-center flex-shrink-0 dark:shadow-[0_0_15px_rgba(138,43,226,0.6)] transition-all duration-500">
                    <span className="text-2xl">💼</span>
                  </div>
                  <div>
                    <h3 className="font-black uppercase text-xs mb-1 dark:text-white transition-colors duration-500">LINKEDIN</h3>
                    <a 
                      href="https://www.linkedin.com/in/connor-madigan/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-blue-600 dark:text-purple-400 hover:underline transition-colors duration-500"
                    >
                      /connor-madigan
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Status Window */}
            <motion.div
              className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(0,255,255,0.5)] transition-all duration-500"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Window Title Bar */}
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 dark:from-cyan-400 dark:to-blue-500 border-b-4 border-black dark:border-cyan-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-black dark:bg-cyan-400 dark:shadow-[0_0_10px_rgba(0,255,255,0.8)]"></div>
                  <span className="font-black text-lg uppercase tracking-tight text-black dark:text-cyan-300 transition-colors duration-500">⚡ STATUS</span>
                </div>
              </div>

              {/* Status Content */}
              <div className="p-6">
                <p className="text-2xl font-black uppercase mb-6 leading-tight dark:text-white transition-colors duration-500">
                  Let's Build Something Great Together
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 p-4 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_10px_rgba(0,255,255,0.5)] transition-all duration-500">
                    <div className="text-4xl font-black text-blue-600 dark:text-cyan-400 mb-1 dark:drop-shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all duration-500">
                      24h
                    </div>
                    <div className="font-black uppercase text-xs dark:text-white transition-colors duration-500">
                      Response Time
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-green-400 p-4 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_10px_rgba(0,255,0,0.5)] transition-all duration-500">
                    <div className="flex items-center justify-center mb-1">
                      <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse dark:shadow-[0_0_15px_rgba(0,255,0,0.8)]"></div>
                      <div className="text-2xl font-black text-green-600 dark:text-green-400 dark:drop-shadow-[0_0_15px_rgba(0,255,0,0.8)] transition-all duration-500">
                        NOW
                      </div>
                    </div>
                    <div className="font-black uppercase text-xs dark:text-white transition-colors duration-500">
                      Available
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-32 right-10 w-20 h-20 bg-yellow-400 border-4 border-yellow-600 dark:border-yellow-400 rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(255,255,0,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute bottom-32 left-10 w-16 h-16 bg-blue-400 dark:bg-cyan-400 border-4 border-blue-600 dark:border-cyan-400 rounded-full hidden lg:block dark:shadow-[0_0_30px_rgba(0,255,255,0.6)] dark:animate-pulse transition-all duration-500"></div>
    </div>
  );
};

export default ContactRetroDual;